import { useState } from 'react'
import { toast } from 'sonner'
import { CreditCard, Loader2 } from 'lucide-react'
import {
  MAX_PUBLIC_NAME_LENGTH,
  setPaymentGatewayPublicName,
  type AdminPaymentGateway,
} from '@/features/payments/adminApi'

interface PaymentGatewayRowProps {
  gateway: AdminPaymentGateway
  /** Cambio de visibilidad: lo persiste el padre (dueño del listado). */
  onToggleVisibility: () => void
  savingVisibility: boolean
  /** Nombre público confirmado por el servidor (ya recortado). */
  onPublicNameSaved: (publicName: string | null) => void
}

/**
 * Una pasarela en la administración del portal: su visibilidad y su NOMBRE
 * PÚBLICO — el que ve el cliente al pagar (goal portal-correcciones-2).
 * El nombre de Zeus se muestra siempre como referencia gris: es el que siguen
 * viendo controlweb, tecopos-admin y las tiendas, y no se edita desde aquí.
 *
 * El borrador es estado LOCAL de la fila (montada con `key={gateway.id}`): si
 * el guardado falla se vuelve al valor anterior, sin quedarse con el optimista.
 */
export function PaymentGatewayRow({
  gateway,
  onToggleVisibility,
  savingVisibility,
  onPublicNameSaved,
}: PaymentGatewayRowProps) {
  const persisted = gateway.publicName ?? ''
  const [draft, setDraft] = useState(persisted)
  const [savingName, setSavingName] = useState(false)
  const isDirty = draft.trim() !== persisted
  const inputId = `public-name-${gateway.id}`

  const savePublicName = async () => {
    setSavingName(true)
    try {
      const { publicName } = await setPaymentGatewayPublicName(
        gateway.id,
        draft.trim() || null,
      )
      // El servidor recorta y decide: la pantalla se queda con SU valor.
      setDraft(publicName ?? '')
      onPublicNameSaved(publicName)
      toast.success(
        publicName
          ? `Los clientes verán «${publicName}»`
          : `${gateway.name} vuelve a mostrarse con su nombre de Zeus`,
      )
    } catch {
      setDraft(persisted)
      toast.error('No se pudo guardar el nombre público')
    } finally {
      setSavingName(false)
    }
  }

  return (
    <li className="rounded-2xl border border-black/5 bg-white px-5 py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-neutral-100">
            <CreditCard className="size-5" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">
              {gateway.publicName ?? gateway.name}
            </p>
            <p className="truncate text-[11px] text-muted-foreground">
              {gateway.publicName ? `${gateway.name} · ` : ''}
              {gateway.code}
            </p>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={gateway.enabled}
          aria-label={`Visibilidad de ${gateway.name}`}
          disabled={savingVisibility}
          onClick={onToggleVisibility}
          className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-40 ${
            gateway.enabled ? 'bg-primary' : 'bg-neutral-300'
          }`}
        >
          <span
            className={`absolute top-0.5 size-5 rounded-full bg-white shadow transition-all ${
              gateway.enabled ? 'left-[22px]' : 'left-0.5'
            }`}
          />
        </button>
      </div>

      <form
        className="mt-3 border-t border-black/5 pt-3"
        onSubmit={(e) => {
          e.preventDefault()
          void savePublicName()
        }}
      >
        <label
          htmlFor={inputId}
          className="text-xs font-semibold text-muted-foreground"
        >
          Nombre público
        </label>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <input
            id={inputId}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={MAX_PUBLIC_NAME_LENGTH}
            disabled={savingName}
            placeholder={gateway.name}
            className="min-w-0 flex-1 rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={!isDirty || savingName}
            className="inline-flex items-center gap-2 rounded-xl border border-black/10 px-3 py-2 text-sm font-semibold hover:border-black/25 disabled:opacity-40 disabled:hover:border-black/10"
          >
            {savingName && <Loader2 className="size-4 animate-spin" />}
            Guardar
          </button>
        </div>
        <p className="mt-1.5 text-[11px] text-muted-foreground">
          Es el nombre que ve el cliente al pagar. Si lo dejas vacío se usa
          «{gateway.name}», el nombre de Zeus.
        </p>
      </form>
    </li>
  )
}
